import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { l } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { ContextualHint } from 'apps/aiAutomation/components/EngagementGoalEditor/components/ContextualHint'
import { EditorLayout } from 'apps/aiAutomation/components/EngagementGoalEditor/components/EditorLayout'
import { EditorStepLayout } from 'apps/aiAutomation/components/EngagementGoalEditor/components/EditorStepLayout'
import { ConfigFormValues } from 'apps/aiAutomation/components/EngagementGoalEditor/components/LeadQualEditor/lib/types'
import { PublishManager } from 'apps/aiAutomation/components/EngagementGoalEditor/components/PublishManager'
import { useSelectedGoalData } from 'apps/aiAutomation/components/EngagementGoalEditor/hooks/useSelectedGoalData'
import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import {
  GoalEditorMode,
  LeadQualEditorStep,
  SubmitRef,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationEngagementGoalSelectors } from 'apps/aiAutomation/store/engagementGoal'
import {
  EngagementGoal,
  GoalStatus,
  GoalType,
  LeadQualExitAction,
} from 'shared/api/requests/aiEngagementGoal/schemas'

import { useLeadQualEditor } from '../../hooks/useLeadQualEditor'
import { ConfigEditor } from '../ConfigEditor'
import { ExitActionEditor } from '../ExitActionEditor'

interface EditorProps {
  onCreate: () => void
}

export const Editor = forwardRef<SubmitRef<EngagementGoal>, EditorProps>(({ onCreate }, ref) => {
  const {
    stepsState,
    configData,
    exitActionData,
    isLoading,
    onConfigFinish,
    onExitActionFinish,
    combineAllData,
  } = useLeadQualEditor({ onCreate })

  const { mode } = useGoalEditorContext(GoalType.LEAD_QUAL)
  const isEditMode = mode !== GoalEditorMode.READ

  const configRef = useRef<SubmitRef<ConfigFormValues>>(null)
  const exitActionRef = useRef<SubmitRef<LeadQualExitAction>>(null)

  const activeGoalsLimitReached = useAppSelector(
    AiAutomationEngagementGoalSelectors.getIsActiveGoalLimitReached,
  )
  const { selectedGoal } = useSelectedGoalData()
  const isPublished = selectedGoal ? selectedGoal.status === GoalStatus.ACTIVE : false
  const isPublishBlocked = activeGoalsLimitReached && !isPublished

  const isConfigStepDimmed =
    !stepsState.completed[LeadQualEditorStep.CONFIG] &&
    stepsState.current !== LeadQualEditorStep.CONFIG

  const isExitActionStepDimmed =
    stepsState.completed[LeadQualEditorStep.CONFIG] &&
    stepsState.current === LeadQualEditorStep.CONFIG

  const isPublishStepDimmed =
    stepsState.completed[LeadQualEditorStep.CONFIG] &&
    [LeadQualEditorStep.CONFIG, LeadQualEditorStep.EXIT_ACTION].includes(stepsState.current)

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        if (!configRef.current?.submit || !exitActionRef.current?.submit) {
          return { isValid: false }
        }

        const config = await configRef.current.submit()
        const exitAction = await exitActionRef.current.submit()

        return combineAllData(config, exitAction)
      },
    }),
    [combineAllData],
  )

  return (
    <div className="o-auto p-x p-y-md">
      <ContextualHint />

      <EditorLayout title={l.translate('Set it up in 3 quick steps')}>
        <EditorStepLayout
          number={1}
          step={LeadQualEditorStep.CONFIG}
          stepsState={stepsState}
          isDimmed={isConfigStepDimmed}
        >
          <ConfigEditor
            isActive={
              stepsState.current === LeadQualEditorStep.CONFIG ||
              (isEditMode && stepsState.completed[LeadQualEditorStep.CONFIG])
            }
            isLoading={isLoading}
            configData={configData}
            onFinish={onConfigFinish}
            ref={configRef}
          />
        </EditorStepLayout>

        <EditorStepLayout
          number={2}
          step={LeadQualEditorStep.EXIT_ACTION}
          stepsState={stepsState}
          isDimmed={isExitActionStepDimmed}
          className="m-t-lg"
        >
          <ExitActionEditor
            configData={configData}
            exitActionData={exitActionData}
            onFinish={onExitActionFinish}
            isActive={
              stepsState.current === LeadQualEditorStep.EXIT_ACTION ||
              (isEditMode && stepsState.completed[LeadQualEditorStep.EXIT_ACTION])
            }
            ref={exitActionRef}
          />
        </EditorStepLayout>

        <EditorStepLayout
          number={3}
          step={LeadQualEditorStep.PUBLISH}
          stepsState={stepsState}
          isDimmed={isPublishStepDimmed}
          isBlocked={isPublishBlocked}
          className="m-t-lg"
        >
          <PublishManager
            type={GoalType.LEAD_QUAL}
            isBlocked={isPublishBlocked}
            isActive={stepsState.current === LeadQualEditorStep.PUBLISH}
          />
        </EditorStepLayout>
      </EditorLayout>
    </div>
  )
})
